import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { supabase } from '@/integrations/supabase/client';
import { ExtractedPage } from './useRecommendationDetection';

interface AgentChatPageParams {
  page: ExtractedPage;
  messageId: string;
  // workbookId and goalThreadId removed - no longer required for private pages
}

/**
 * Hook for creating pages from agent chat recommendations
 * Now routes to private text library instead of project-based pages
 * Simplified flow - creates private pages that can be assigned to projects later
 */
export const useAgentChatPageCreation = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ page, messageId }: AgentChatPageParams) => {
      console.log('[AgentChatPage] Starting private page creation for:', page.title);
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Create private page (no project context)
      const { data, error } = await supabase
        .from('workbook_page_content')
        .insert({
          workbook_id: null, // Private page - no project association
          title: page.title,
          content: '', // Empty content - user will add their own
          initial_request: page.description,
          type: page.type,
          user_id: user.id
          // No openai_thread_id for private pages
        })
        .select()
        .single();

      if (error) {
        console.error('[AgentChatPage] Database error:', error);
        throw error;
      }

      console.log('[AgentChatPage] Private page created successfully:', data.id);
      return data;
    },
    onSuccess: (data, variables) => {
      console.log('[AgentChatPage] Success, invalidating private page queries');
      
      // Invalidate private text library queries
      queryClient.invalidateQueries({ 
        queryKey: ['private-text-library'] 
      });
      
      queryClient.invalidateQueries({ 
        queryKey: ['workbook-page-content', 'private'] 
      });

      // Also invalidate general page content queries
      queryClient.invalidateQueries({ 
        queryKey: ['workbook-page-content'] 
      });
    },
    onError: (error) => {
      console.error('[AgentChatPage] Error:', error);
    },
  });
}; 